/**
 * CostCurveChart — "What an annual increase does to the bill"
 * Pure arithmetic on a number the visitor enters: year-one subscription,
 * two "Annual increase" rates (default 3% and 10%), a 3- or 5-year term.
 * Draws annual subscription by year for both rates with the gap shaded and
 * reads out the cumulative difference. No Oracle price data anywhere; the only
 * attributed rates are Vendr's two published sentences shown under the sliders.
 *
 * Data: components/netsuite-pricing/data/cost-curve.json (read 2026-10-02).
 * All visible copy lives in STR so a Traditional Chinese version can be added.
 *
 * Self-check against worked_figures_used_on_the_page (year n = S * (1 + r)^(n - 1),
 * gap = cumulative(10%) - cumulative(3%); year one is identical, so the gap is
 * entirely years two and three):
 *   S = 585,000   3%: 585,000 + 602,550.0 + 620,626.5 = 1,808,176.5
 *                10%: 585,000 + 643,500.0 + 707,850.0 = 1,936,350.0
 *                gap = 128,173.5  -> rounds to HK$128,174   (matches JSON)
 *   S = 234,000   3%: 234,000 + 241,020.0 + 248,250.6 =   723,270.6
 *                10%: 234,000 + 257,400.0 + 283,140.0 =   774,540.0
 *                gap =  51,269.4  -> rounds to HK$51,269    (matches JSON)
 */
'use client';

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import data from '@/components/netsuite-pricing/data/cost-curve.json';

const PSGLOBAL_LOGO =
  'https://images.prismic.io/onepac/ag7UFaYofJOwHelQ_psglobal_color.png?auto=format,compress';

/* ------------------------------- Strings ------------------------------- */
const STR = {
  title: 'What an annual increase does to the bill',
  sub: 'Enter a year-one subscription and compare two annual increase rates over the term. Arithmetic only, on your own number.',
  logoAlt: 'PS Global Consulting',
  currencyGroup: 'Currency',
  hkd: 'HKD',
  usd: 'USD',
  amountLabel: 'Year-one subscription',
  amountNote: "Defaults to the top of Vendr's 10 to 25 user band. Replace it with your own quote. HKD and USD converted at 7.8.",
  presetsLabel: "Presets from Vendr's published bands",
  presets: [
    'Vendr, 10 to 25 users, low',
    'Vendr, 10 to 25 users, high',
    'Vendr, 25 to 100 users, low',
    'Vendr, 25 to 100 users, high',
  ],
  increaseLabel: 'Annual increase',
  rateFirst: 'first rate',
  rateSecond: 'second rate',
  termLabel: 'Term',
  years: '{n} years',
  readout: 'Over {n} years the difference between {lo}% and {hi}% is {amt}',
  readoutSame: 'Pick two different rates to see a gap over {n} years.',
  readoutEmpty: 'Enter a year-one subscription to see the curve.',
  yearOneNote: 'Year one is the same under both rates; the gap builds from year two.',
  cumulative: 'Cumulative over {n} years: {a} at {lo}%, {b} at {hi}%.',
  chartAria: 'Annual subscription by year under two annual increase rates, with the gap between them shaded',
  year: 'Year {n}',
  yearShort: 'Y{n}',
  gap: 'gap',
  anchorsTitle: "Vendr's two published reference points",
  anchorsNote: "The only attributed rates on this chart are Vendr's. Your contract may say something else.",
  anchors: [
    'Range Vendr reports where nothing was negotiated',
    'Cap Vendr suggests asking for',
  ],
  tryAnchor: 'Set sliders to {lo}% and {hi}%',
  to: 'to',
  caption: 'Arithmetic on the number you enter. Not Oracle prices, not a PS Global quote.',
  source: 'Reference rates and presets: ',
  checked: 'Checked 2 Oct 2026.',
};

// Fills {key} slots; keys not supplied are left in place so a caller can split on them.
const tpl = (s: string, vars: Record<string, string | number>): string =>
  s.replace(/\{(\w+)\}/g, (m, k: string) => (vars[k] === undefined ? m : String(vars[k])));

/* -------------------------------- Data --------------------------------- */
type Currency = 'hkd' | 'usd';
const FX = data.inputs.year1_subscription.fx;
const DEFAULT_HKD = data.inputs.year1_subscription.default_hkd;
const PRESETS = data.inputs.year1_subscription.presets;
const SLIDER = data.inputs.annual_increase_pct;
const TERMS = data.inputs.term_years.options;
const DEFAULT_TERM = data.inputs.term_years.default;
const ANCHORS = data.rate_anchors;
const RATE_A = 3;
const RATE_B = 10;
const MAX_AMOUNT = 1e9;

const series = (s: number, pct: number, n: number): number[] =>
  Array.from({ length: n }, (_, i) => s * Math.pow(1 + pct / 100, i));
const sum = (xs: number[]): number => xs.reduce((a, b) => a + b, 0);

const withCommas = (v: number): string => Math.round(v).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
const money = (v: number, cur: Currency): string => `${cur === 'hkd' ? 'HK$' : 'US$'}${withCommas(v)}`;
const short = (v: number): string => {
  if (v >= 1e6) return `${(v / 1e6).toFixed(2).replace(/\.?0+$/, '')}M`;
  if (v >= 1e3) return `${(v / 1e3).toFixed(1).replace(/\.0$/, '')}k`;
  return withCommas(v);
};
const pct = (r: number): string => (Number.isInteger(r) ? String(r) : r.toFixed(1));

function niceTicks(min: number, max: number): number[] {
  const span = Math.max(max - min, 1);
  const raw = span / 4;
  const mag = Math.pow(10, Math.floor(Math.log10(raw)));
  const step = [1, 2, 2.5, 5, 10].map((m) => m * mag).find((c) => c >= raw) ?? raw;
  let lo = Math.floor(min / step) * step;
  if (min - lo < step * 0.3) lo -= step;
  let hi = Math.ceil(max / step) * step;
  if (hi - max < step * 0.3) hi += step;
  lo = Math.max(lo, 0);
  const ticks: number[] = [];
  for (let t = lo; t <= hi + step / 2; t += step) ticks.push(t);
  return ticks;
}

/* --------------------------- Scoped styles ----------------------------- */
const STYLE_ID = 'ccc-styles';
const CSS = `
.ccc-root { --ccc-font: 'Poppins', system-ui, -apple-system, sans-serif;
  --ink: #1B2A33; --ink-2: #3E5460; --body: #5B6B75; --muted: #8A99A3;
  --line: #E4E7EA; --paper: #FFFFFF; --wash: #F5F8F8;
  --teal: #0f9993; --teal-deep: #0d555c; --teal-900: #04445c; --gold: #B08A2E; --gold-deep: #7A5F1D;
  position: relative; box-sizing: border-box; width: 100%; max-width: 860px;
  background: var(--paper); border: 1px solid var(--line); border-radius: 20px;
  box-shadow: 0 10px 20px -10px rgba(4,68,92,0.35);
  padding: 40px 44px 28px; color: var(--ink);
  font-family: var(--ccc-font); -webkit-font-smoothing: antialiased; }
.ccc-root *, .ccc-root *::before, .ccc-root *::after { box-sizing: border-box; margin: 0; padding: 0; }
.ccc-root p { letter-spacing: normal; }
.ccc-root .head { display: flex; justify-content: space-between; align-items: flex-start; gap: 24px; }
.ccc-root .ccc-logo { height: 40px; width: auto; flex: none; }
.ccc-root h2 { font-size: 26px; font-weight: 600; letter-spacing: -0.01em; line-height: 1.3; max-width: 560px; color: var(--ink); }
.ccc-root .sub { font-size: 14px; color: var(--body); margin-top: 8px; max-width: 620px; line-height: 1.55; }
.ccc-root .panel { margin-top: 24px; display: grid; grid-template-columns: minmax(0, 1fr) minmax(0, 1fr); gap: 22px 32px; }
.ccc-root .flabel { display: block; font-size: 11px; font-weight: 600; letter-spacing: .06em; text-transform: uppercase; color: var(--teal-deep); margin-bottom: 8px; }
.ccc-root .amount { display: flex; gap: 10px; align-items: stretch; }
.ccc-root .amount input { flex: 1 1 auto; min-width: 0; width: 100%; height: 44px; padding: 0 14px; border: 1px solid var(--line); border-radius: 12px; font: 600 18px var(--ccc-font); color: var(--ink); font-variant-numeric: tabular-nums; background: var(--paper); transition: border-color .15s, box-shadow .15s; }
.ccc-root .amount input:focus { outline: none; border-color: var(--teal); box-shadow: 0 0 0 3px rgba(15,153,147,.18); }
.ccc-root .seg { display: inline-flex; border: 1px solid var(--line); border-radius: 999px; padding: 3px; gap: 2px; background: var(--paper); align-self: center; flex: none; }
.ccc-root .seg button { border: 0; background: none; font: 600 12px var(--ccc-font); padding: 6px 13px; border-radius: 999px; color: var(--ink-2); cursor: pointer; transition: background .15s, color .15s; line-height: 1.2; white-space: nowrap; }
.ccc-root .seg button[aria-pressed="true"] { background: var(--teal-900); color: #fff; }
.ccc-root .seg button:focus-visible { outline: 2px solid var(--teal); outline-offset: 1px; }
.ccc-root .note { font-size: 11.5px; color: var(--muted); line-height: 1.5; margin-top: 8px; }
.ccc-root .chips { display: flex; flex-wrap: wrap; gap: 6px; margin-top: 8px; }
.ccc-root .chip { border: 1px solid var(--line); background: var(--wash); border-radius: 999px; padding: 5px 10px; font: 500 11.5px var(--ccc-font); color: var(--ink-2); cursor: pointer; line-height: 1.3; transition: background .15s, border-color .15s, color .15s; text-align: left; }
.ccc-root .chip b { font-weight: 600; color: var(--ink); font-variant-numeric: tabular-nums; }
.ccc-root .chip:hover { border-color: var(--teal); }
.ccc-root .chip[aria-pressed="true"] { background: var(--teal-900); border-color: var(--teal-900); color: #fff; }
.ccc-root .chip[aria-pressed="true"] b { color: #fff; }
.ccc-root .chip:focus-visible { outline: 2px solid var(--teal); outline-offset: 1px; }
.ccc-root .slider { display: block; margin-bottom: 14px; }
.ccc-root .slider:last-child { margin-bottom: 0; }
.ccc-root .sl-head { display: flex; justify-content: space-between; align-items: baseline; font-size: 13px; color: var(--ink-2); margin-bottom: 4px; }
.ccc-root .sl-head i { display: inline-block; width: 14px; height: 4px; border-radius: 2px; margin-right: 8px; vertical-align: middle; }
.ccc-root .slider.a .sl-head i { background: var(--teal); }
.ccc-root .slider.b .sl-head i { background: var(--teal-900); }
.ccc-root .sl-head output { font-weight: 600; color: var(--ink); font-size: 16px; font-variant-numeric: tabular-nums; }
.ccc-root input[type=range] { width: 100%; accent-color: var(--teal); cursor: pointer; height: 24px; }
.ccc-root .slider.b input[type=range] { accent-color: var(--teal-900); }
.ccc-root input[type=range]:focus-visible { outline: 2px solid var(--gold); outline-offset: 2px; border-radius: 4px; }
.ccc-root .anchors { margin-top: 16px; border-top: 1px solid var(--line); padding-top: 12px; }
.ccc-root .anchors-title { font-size: 11px; font-weight: 600; letter-spacing: .06em; text-transform: uppercase; color: var(--teal-deep); }
.ccc-root .anchor { margin-top: 10px; display: grid; grid-template-columns: auto minmax(0, 1fr); gap: 2px 12px; align-items: baseline; }
.ccc-root .anchor .rng { font-size: 15px; font-weight: 600; color: var(--gold-deep); font-variant-numeric: tabular-nums; white-space: nowrap; grid-row: span 3; }
.ccc-root .anchor .lab { font-size: 12.5px; font-weight: 600; color: var(--ink); line-height: 1.4; }
.ccc-root .anchor .quote { font-size: 12px; color: var(--body); line-height: 1.5; }
.ccc-root .anchor .quote a { color: var(--teal-deep); text-decoration: underline; text-underline-offset: 2px; }
.ccc-root .anchor .quote a:hover { color: var(--teal); }
.ccc-root .anchor .try { border: 0; background: none; padding: 0; font: 500 11.5px var(--ccc-font); color: var(--teal); cursor: pointer; text-decoration: underline; text-underline-offset: 2px; text-align: left; }
.ccc-root .anchor .try:hover { color: var(--teal-deep); }
.ccc-root .anchor .try:focus-visible { outline: 2px solid var(--teal); outline-offset: 2px; border-radius: 3px; }
.ccc-root .anchors-note { font-size: 11.5px; color: var(--muted); margin-top: 10px; line-height: 1.5; }
.ccc-root .readout { margin-top: 26px; display: flex; justify-content: space-between; align-items: flex-start; gap: 16px 24px; flex-wrap: wrap; }
.ccc-root .readout p.big { font-size: 20px; font-weight: 600; line-height: 1.35; color: var(--ink); max-width: 560px; letter-spacing: -0.01em; }
.ccc-root .readout p.big b { color: var(--gold-deep); font-variant-numeric: tabular-nums; }
.ccc-root .readout .small { font-size: 12.5px; color: var(--body); margin-top: 6px; line-height: 1.55; }
.ccc-root .chartwrap { position: relative; margin-top: 16px; }
.ccc-root .chartwrap svg { display: block; width: 100%; height: auto; overflow: visible; font-family: var(--ccc-font); }
.ccc-root .chartwrap .yl { font-size: 10.5px; fill: var(--muted); font-variant-numeric: tabular-nums; }
.ccc-root .chartwrap .xl { font-size: 11px; fill: var(--ink-2); }
.ccc-root .chartwrap .el { font-size: 11.5px; font-weight: 600; fill: var(--ink); font-variant-numeric: tabular-nums; }
.ccc-root .chartwrap .el.a { fill: var(--teal); }
.ccc-root .chartwrap .el.b { fill: var(--teal-900); }
.ccc-root .chartwrap .hit { fill: transparent; cursor: default; }
.ccc-root .chartwrap .hit:focus { outline: none; }
.ccc-root .chartwrap .hit:focus-visible { outline: 2px solid var(--gold); outline-offset: -2px; }
.ccc-root .tip { position: absolute; z-index: 5; width: 230px; max-width: calc(100% - 16px); background: var(--ink); color: #fff; border-radius: 10px; padding: 10px 12px; font-size: 12px; line-height: 1.55; pointer-events: none; opacity: 0; transform: translateY(4px); transition: opacity .15s, transform .15s; box-shadow: 0 12px 30px -12px rgba(0,0,0,0.5); font-variant-numeric: tabular-nums; }
.ccc-root .tip.on { opacity: 1; transform: none; }
.ccc-root .tip b { display: block; color: #A9D9D4; font-size: 10.5px; letter-spacing: .1em; text-transform: uppercase; margin-bottom: 4px; font-weight: 600; }
.ccc-root .tip .row { display: flex; justify-content: space-between; gap: 12px; }
.ccc-root .tip .row.gap { border-top: 1px solid rgba(255,255,255,.18); margin-top: 4px; padding-top: 4px; color: #E8D9A8; font-weight: 600; }
.ccc-root .foot { margin-top: 20px; padding-top: 16px; border-top: 1px solid var(--line); }
.ccc-root .foot .cap { font-size: 12.5px; line-height: 1.6; color: var(--body); max-width: 720px; }
.ccc-root .foot .cap b { font-weight: 600; color: var(--ink); }
.ccc-root .foot a { color: var(--teal-deep); text-decoration: underline; text-underline-offset: 2px; }
.ccc-root .foot a:hover { color: var(--teal); }
.ccc-root.compact { padding: 24px 16px 20px; border-radius: 0; border-left: none; border-right: none; box-shadow: none; }
.ccc-root.compact .ccc-logo { display: none; }
.ccc-root.compact h2 { font-size: 21px; }
.ccc-root.compact .sub { font-size: 13px; }
.ccc-root.compact .panel { grid-template-columns: 1fr; gap: 20px; margin-top: 20px; }
.ccc-root.compact .amount input { font-size: 16px; }
.ccc-root.compact .readout p.big { font-size: 17px; }
.ccc-root.compact .anchor { grid-template-columns: 1fr; }
.ccc-root.compact .anchor .rng { grid-row: auto; }`;

function useInjectedStyles(): void {
  useEffect(() => {
    const existing = document.getElementById(STYLE_ID);
    if (existing) {
      if (existing.textContent !== CSS) existing.textContent = CSS; // refresh stale CSS after hot-reload
    } else {
      const s = document.createElement('style');
      s.id = STYLE_ID; s.textContent = CSS;
      document.head.appendChild(s);
    }
  }, []);
}

/* ------------------------------ Component ------------------------------ */
export interface CostCurveChartProps { compact?: boolean; }

const CostCurveChart: React.FC<CostCurveChartProps> = ({ compact = false }) => {
  useInjectedStyles();
  const rootRef = useRef<HTMLDivElement>(null);
  const wrapRef = useRef<HTMLDivElement>(null);
  const [narrow, setNarrow] = useState(false);
  const [chartW, setChartW] = useState(640);
  const [cur, setCur] = useState<Currency>('hkd');
  const [amountHkd, setAmountHkd] = useState<number>(DEFAULT_HKD);
  const [rateA, setRateA] = useState<number>(RATE_A);
  const [rateB, setRateB] = useState<number>(RATE_B);
  const [term, setTerm] = useState<number>(DEFAULT_TERM);
  const [hoverYear, setHoverYear] = useState<number | null>(null);
  const isCompact = compact || narrow;

  useEffect(() => {
    const el = rootRef.current;
    const wrap = wrapRef.current;
    if (!el) return;
    const measure = (): void => {
      setNarrow(el.offsetWidth < 600);
      if (wrap) setChartW(Math.max(280, wrap.offsetWidth));
    };
    measure();
    window.addEventListener('resize', measure);
    let ro: ResizeObserver | undefined;
    if (typeof ResizeObserver !== 'undefined') { ro = new ResizeObserver(measure); ro.observe(el); }
    return () => { window.removeEventListener('resize', measure); if (ro) ro.disconnect(); };
  }, []);

  /* --- Derived figures (all in the display currency) --- */
  const amount = cur === 'hkd' ? amountHkd : amountHkd / FX;
  const lo = Math.min(rateA, rateB);
  const hi = Math.max(rateA, rateB);
  const sA = useMemo(() => series(amount, lo, term), [amount, lo, term]);
  const sB = useMemo(() => series(amount, hi, term), [amount, hi, term]);
  const cumA = sum(sA);
  const cumB = sum(sB);
  const gap = cumB - cumA;

  const onAmount = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const digits = e.target.value.replace(/[^\d]/g, '');
    const n = Math.min(MAX_AMOUNT, digits ? Number(digits) : 0);
    setAmountHkd(cur === 'hkd' ? n : n * FX);
  };
  const setRates = useCallback((a: number, b: number): void => { setRateA(a); setRateB(b); }, []);

  /* --- SVG geometry --- */
  const H = isCompact ? 240 : 300;
  const ml = 46, mr = isCompact ? 78 : 96, mt = 20, mb = 30;
  const pw = Math.max(40, chartW - ml - mr);
  const ph = H - mt - mb;
  const yTicks = useMemo(() => (amount > 0 ? niceTicks(amount, sB[sB.length - 1]) : []), [amount, sB]);
  const yMin = yTicks[0] ?? 0;
  const yMax = yTicks[yTicks.length - 1] ?? 1;
  const x = (i: number): number => ml + (term === 1 ? 0 : (i / (term - 1)) * pw);
  const y = (v: number): number => mt + (1 - (v - yMin) / (yMax - yMin || 1)) * ph;
  const lineA = sA.map((v, i) => `${i ? 'L' : 'M'}${x(i).toFixed(1)},${y(v).toFixed(1)}`).join(' ');
  const lineB = sB.map((v, i) => `${i ? 'L' : 'M'}${x(i).toFixed(1)},${y(v).toFixed(1)}`).join(' ');
  const area = `${lineB} ${sA.map((v, i) => `L${x(sA.length - 1 - i).toFixed(1)},${y(sA[sA.length - 1 - i]).toFixed(1)}`).join(' ')} Z`;
  // End labels: keep the two from overlapping when the rates are close.
  const last = term - 1;
  let yEndA = y(sA[last]);
  let yEndB = y(sB[last]);
  if (Math.abs(yEndA - yEndB) < 14) { const mid = (yEndA + yEndB) / 2; yEndB = mid - 7; yEndA = mid + 7; }

  const tipX = hoverYear !== null ? Math.max(8, Math.min(chartW - 238, x(hoverYear) - 115)) : 0;

  // The readout sentence is split around {amt} so the figure can be emphasised.
  const readout = amount <= 0
    ? STR.readoutEmpty
    : lo === hi
      ? tpl(STR.readoutSame, { n: term })
      : tpl(STR.readout, { n: term, lo: pct(lo), hi: pct(hi) });
  const [readBefore, readAfter] = readout.split('{amt}');

  return (
    <div className={`ccc-root${isCompact ? ' compact' : ''}`} ref={rootRef}>
      <div className="head">
        <div>
          <h2>{STR.title}</h2>
          <p className="sub">{STR.sub}</p>
        </div>
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img className="ccc-logo" src={PSGLOBAL_LOGO} alt={STR.logoAlt} />
      </div>

      <div className="panel">
        <div>
          <label className="flabel" htmlFor="ccc-amount">{STR.amountLabel}</label>
          <div className="amount">
            <div className="seg" role="group" aria-label={STR.currencyGroup}>
              <button type="button" aria-pressed={cur === 'hkd'} onClick={() => setCur('hkd')}>{STR.hkd}</button>
              <button type="button" aria-pressed={cur === 'usd'} onClick={() => setCur('usd')}>{STR.usd}</button>
            </div>
            <input
              id="ccc-amount"
              type="text"
              inputMode="numeric"
              autoComplete="off"
              value={amount > 0 ? withCommas(amount) : ''}
              onChange={onAmount}
              aria-describedby="ccc-amount-note"
            />
          </div>
          <p className="note" id="ccc-amount-note">{STR.amountNote}</p>
          <p className="flabel" style={{ marginTop: 14 }}>{STR.presetsLabel}</p>
          <div className="chips">
            {PRESETS.map((p, i) => {
              const active = Math.round(amountHkd) === p.hkd;
              return (
                <button
                  type="button"
                  className="chip"
                  key={p.label}
                  aria-pressed={active}
                  onClick={() => setAmountHkd(p.hkd)}
                >
                  {STR.presets[i] ?? p.label} <b>{money(cur === 'hkd' ? p.hkd : p.usd, cur)}</b>
                </button>
              );
            })}
          </div>
        </div>

        <div>
          <label className="slider a">
            <span className="sl-head">
              <span><i aria-hidden="true"></i>{STR.increaseLabel}</span>
              <output aria-live="polite">{pct(rateA)}%</output>
            </span>
            <input
              type="range" min={SLIDER.min} max={SLIDER.max} step={SLIDER.step} value={rateA}
              aria-label={`${STR.increaseLabel}, ${STR.rateFirst}`}
              aria-valuetext={`${pct(rateA)}%`}
              onChange={(e) => setRateA(Number(e.target.value))}
            />
          </label>
          <label className="slider b">
            <span className="sl-head">
              <span><i aria-hidden="true"></i>{STR.increaseLabel}</span>
              <output aria-live="polite">{pct(rateB)}%</output>
            </span>
            <input
              type="range" min={SLIDER.min} max={SLIDER.max} step={SLIDER.step} value={rateB}
              aria-label={`${STR.increaseLabel}, ${STR.rateSecond}`}
              aria-valuetext={`${pct(rateB)}%`}
              onChange={(e) => setRateB(Number(e.target.value))}
            />
          </label>

          <div className="anchors">
            <p className="anchors-title">{STR.anchorsTitle}</p>
            {ANCHORS.map((a, i) => (
              <div className="anchor" key={a.verbatim}>
                <span className="rng">{a.low_pct} {STR.to} {a.high_pct}%</span>
                <span className="lab">{STR.anchors[i] ?? a.label}</span>
                <span className="quote">
                  &ldquo;{a.verbatim}&rdquo;{' '}
                  <a href={a.url} target="_blank" rel="noopener noreferrer">{a.source}</a>
                </span>
                <button type="button" className="try" onClick={() => setRates(a.low_pct, a.high_pct)}>
                  {tpl(STR.tryAnchor, { lo: a.low_pct, hi: a.high_pct })}
                </button>
              </div>
            ))}
            <p className="anchors-note">{STR.anchorsNote}</p>
          </div>
        </div>
      </div>

      <div className="readout">
        <div>
          <p className="big" aria-live="polite">
            {readBefore}
            {readAfter !== undefined && <b>{money(gap, cur)}</b>}
            {readAfter}
          </p>
          {amount > 0 && lo !== hi && (
            <p className="small">
              {tpl(STR.cumulative, { n: term, a: money(cumA, cur), b: money(cumB, cur), lo: pct(lo), hi: pct(hi) })} {STR.yearOneNote}
            </p>
          )}
        </div>
        <div className="seg" role="group" aria-label={STR.termLabel}>
          {TERMS.map((t) => (
            <button type="button" key={t} aria-pressed={term === t} onClick={() => { setTerm(t); setHoverYear(null); }}>
              {tpl(STR.years, { n: t })}
            </button>
          ))}
        </div>
      </div>

      <div className="chartwrap" ref={wrapRef}>
        <svg viewBox={`0 0 ${chartW} ${H}`} width={chartW} height={H} role="img" aria-label={STR.chartAria}>
          {amount > 0 && (
            <>
              {yTicks.map((t) => (
                <g key={t}>
                  <line x1={ml} x2={ml + pw} y1={y(t)} y2={y(t)} stroke="#E4E7EA" strokeWidth={1} />
                  <text className="yl" x={ml - 8} y={y(t) + 3.5} textAnchor="end">{short(t)}</text>
                </g>
              ))}
              <text className="yl" x={ml - 8} y={mt - 8} textAnchor="end">{cur === 'hkd' ? 'HK$' : 'US$'}</text>
              <path d={area} fill="rgba(176,138,46,0.18)" />
              <path d={lineA} fill="none" stroke="#0f9993" strokeWidth={2.5} strokeLinejoin="round" strokeLinecap="round" />
              <path d={lineB} fill="none" stroke="#04445c" strokeWidth={2.5} strokeLinejoin="round" strokeLinecap="round" />
              {hoverYear !== null && (
                <line x1={x(hoverYear)} x2={x(hoverYear)} y1={mt} y2={mt + ph} stroke="#B08A2E" strokeWidth={1} strokeDasharray="3 3" />
              )}
              {sA.map((v, i) => (
                <g key={i}>
                  <circle cx={x(i)} cy={y(v)} r={hoverYear === i ? 5 : 3.5} fill="#fff" stroke="#0f9993" strokeWidth={2} />
                  <circle cx={x(i)} cy={y(sB[i])} r={hoverYear === i ? 5 : 3.5} fill="#fff" stroke="#04445c" strokeWidth={2} />
                  <text className="xl" x={x(i)} y={H - 10} textAnchor="middle">
                    {tpl(isCompact ? STR.yearShort : STR.year, { n: i + 1 })}
                  </text>
                </g>
              ))}
              <text className="el b" x={x(last) + 10} y={yEndB + 4}>{pct(hi)}%: {money(sB[last], cur)}</text>
              <text className="el a" x={x(last) + 10} y={yEndA + 4}>{pct(lo)}%: {money(sA[last], cur)}</text>
              {sA.map((v, i) => {
                const half = term === 1 ? pw / 2 : pw / (term - 1) / 2;
                const ariaYear = `${tpl(STR.year, { n: i + 1 })}: ${pct(lo)}% ${money(v, cur)}, ${pct(hi)}% ${money(sB[i], cur)}, ${STR.gap} ${money(sB[i] - v, cur)}`;
                return (
                  <rect
                    key={i}
                    className="hit"
                    x={x(i) - half} y={mt} width={half * 2} height={ph}
                    tabIndex={0}
                    role="img"
                    aria-label={ariaYear}
                    onMouseEnter={() => setHoverYear(i)}
                    onMouseLeave={() => setHoverYear(null)}
                    onFocus={() => setHoverYear(i)}
                    onBlur={() => setHoverYear(null)}
                    onKeyDown={(e) => { if (e.key === 'Escape') setHoverYear(null); }}
                  />
                );
              })}
            </>
          )}
        </svg>
        <div className={`tip${hoverYear !== null ? ' on' : ''}`} role="tooltip" style={{ left: tipX, top: mt + 6 }}>
          {hoverYear !== null && (
            <>
              <b>{tpl(STR.year, { n: hoverYear + 1 })}</b>
              <div className="row"><span>{pct(lo)}%</span><span>{money(sA[hoverYear], cur)}</span></div>
              <div className="row"><span>{pct(hi)}%</span><span>{money(sB[hoverYear], cur)}</span></div>
              <div className="row gap"><span>{STR.gap}</span><span>{money(sB[hoverYear] - sA[hoverYear], cur)}</span></div>
            </>
          )}
        </div>
      </div>

      <div className="foot">
        <p className="cap">
          <b>{STR.caption}</b> {STR.source}
          <a href={ANCHORS[0].url} target="_blank" rel="noopener noreferrer">{ANCHORS[0].source}</a>. {STR.checked}
        </p>
      </div>
    </div>
  );
};

export default CostCurveChart;
